/**
 * Route-level feed-need fallback (consumer feed filters, Phase 2).
 *
 * The Phase-2 `sports` allow-list is built from what mounted sources declare
 * they render. The Value Dashboard and a game detail declare narrow sets;
 * other market consumers — positions, trades, research, bots — may need any
 * sport, and must not be starved by a sibling tab's narrow declaration.
 * Settings and verified administrative pages render no market rows: their
 * fallback must not widen the shared feed for every trading tab (#853).
 * Unknown routes remain broad until their consumers have been audited.
 * Inert (declares nothing worth sending) unless
 * VITE_FEED_FILTERS_SPORTS is on; class/type opt-ins are unaffected.
 */
import { useLocation } from 'react-router';
import { useFeedNeeds } from '@/hooks/useFeedNeeds';

// Routes whose view declares its own sport(s): the dashboard (selected chip)
// and a game detail (the game's sport).
const SPORT_SCOPED_PREFIXES = ['/dashboard', '/game'];

// Exact paths, not whole groups. In particular Organization includes live
// account/trader views, and /live does not own a declaration on this branch.
// G1 Health reads relay diagnostics and transport metadata, not market rows.
const NON_MARKET_ROUTES = new Set([
  '/settings',
  '/administration',
  '/administration/management',
  '/administration/g1-health',
]);

export function FeedNeedsRouteFallback() {
  const { pathname } = useLocation();
  const scoped = SPORT_SCOPED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  const nonMarket = NON_MARKET_ROUTES.has(pathname.replace(/\/+$/, ''));
  useFeedNeeds('route', scoped || nonMarket ? null : { sports: 'all' });
  return null;
}
